/**
 * Programs List Page - Browse all study abroad programs
 * /programs
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { usePageContent, getSection } from '@/hooks/usePageContent';
import SEO from '@/components/SEO';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  GraduationCap,
  MapPin,
  Clock,
  DollarSign,
  Search,
  Filter,
  ChevronRight,
  Star,
  Loader2,
  BookOpen
} from 'lucide-react';

const API_URL = window.location.origin || '';

const ProgramsListPage = () => {
  const { isDark } = useTheme();
  const [programs, setPrograms] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDestination, setSelectedDestination] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedField, setSelectedField] = useState('all');
  const [fetchError, setFetchError] = useState(false);

  // CMS Page Content (Phase C wiring)
  const { content: cmsContent } = usePageContent('programs-list');
  const heroContent = getSection(cmsContent, 'hero', {
    badge: 'Popular Courses',
    title: 'Study Abroad Programs',
    subtitle: 'Discover top courses in Computer Science, Business, Medicine, Engineering, and more at world-renowned universities',
    search_placeholder: 'Search programs (e.g., MS Computer Science, MBA, MBBS...)'
  });
  const emptyState = getSection(cmsContent, 'empty_state', {
    title: 'No programs found',
    subtitle: 'Try adjusting your filters or search term',
    clear_label: 'Clear All Filters'
  });
  const finalCta = getSection(cmsContent, 'final_cta', {
    title: 'Not Sure Which Program is Right for You?',
    subtitle: 'Our AI counselor can help you find the perfect program based on your profile, interests, and career goals.',
    cta_primary_text: 'Talk to AI Counselor',
    cta_primary_link: '/contact?intent=ai-counselor',
    cta_secondary_text: 'Book Free Consultation',
    cta_secondary_link: '/contact'
  });

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        setFetchError(false);
        const res = await fetch(`${API_URL}/api/programs/public`);
        if (res.ok) {
          const data = await res.json();
          setPrograms(data);
          setFilteredPrograms(data);
        } else {
          setFetchError(true);
        }
      } catch (error) {
        console.error('Error fetching programs:', error);
        setFetchError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPrograms();
  }, []);

  // Filter programs based on search and filters
  useEffect(() => {
    let filtered = [...programs];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(term) ||
        p.destination.toLowerCase().includes(term) ||
        p.field_of_study.toLowerCase().includes(term)
      );
    }

    if (selectedDestination !== 'all') {
      filtered = filtered.filter(p => p.destination === selectedDestination);
    }

    if (selectedLevel !== 'all') {
      filtered = filtered.filter(p => p.degree_level === selectedLevel);
    }

    if (selectedField !== 'all') {
      filtered = filtered.filter(p => p.field_of_study === selectedField);
    }

    setFilteredPrograms(filtered);
  }, [searchTerm, selectedDestination, selectedLevel, selectedField, programs]);

  // Get unique values for filters
  const destinations = [...new Set(programs.map(p => p.destination))].sort();
  const levels = [...new Set(programs.map(p => p.degree_level))].sort();
  const fields = [...new Set(programs.map(p => p.field_of_study))].sort();

  // Featured programs
  const featuredPrograms = programs.filter(p => p.is_featured).slice(0, 3);

  return (
    <>
      <SEO 
        title="Study Abroad Programs - Courses in USA, UK, Canada, Australia, Germany"
        description="Explore top study abroad programs including MS in Computer Science, MBA, Engineering, and more. Find the perfect course in USA, UK, Canada, Australia, or Germany for Indian students."
        keywords="study abroad programs, MS USA, MBA Canada, engineering Australia, data science UK, study abroad courses Indian students"
        canonical="https://unixperts.com/programs"
      />
      <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`}>
      {/* Hero Section */}
      <section className={`py-12 md:py-20 ${isDark ? 'bg-gradient-to-br from-[#1a2830] to-[#0d1117]' : 'bg-gradient-to-br from-ux-blue to-ux-cyan'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-white/20 text-white mb-4" data-testid="programs-badge">
            {heroContent.badge}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-display" data-testid="programs-title">
            {heroContent.title}
          </h1>
          <p className="text-xl text-white/80 max-w-3xl mx-auto mb-8">
            {heroContent.subtitle}
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder={heroContent.search_placeholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 py-6 text-lg rounded-full bg-white border-0 shadow-lg"
              data-testid="programs-search"
            />
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className={`py-6 border-b ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className={`w-4 h-4 ${isDark ? 'text-white/60' : 'text-slate-500'}`} />
              <span className={`text-sm font-medium ${isDark ? 'text-white/80' : 'text-slate-700'}`}>Filter:</span>
            </div>

            <Select value={selectedDestination} onValueChange={setSelectedDestination}>
              <SelectTrigger className={`w-[160px] ${isDark ? 'bg-white/10 border-white/20' : ''}`} data-testid="filter-destination">
                <SelectValue placeholder="Destination" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Destinations</SelectItem>
                {destinations.map(d => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedLevel} onValueChange={setSelectedLevel}>
              <SelectTrigger className={`w-[160px] ${isDark ? 'bg-white/10 border-white/20' : ''}`} data-testid="filter-level">
                <SelectValue placeholder="Degree Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                {levels.map(l => (
                  <SelectItem key={l} value={l}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedField} onValueChange={setSelectedField}>
              <SelectTrigger className={`w-[180px] ${isDark ? 'bg-white/10 border-white/20' : ''}`} data-testid="filter-field">
                <SelectValue placeholder="Field of Study" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Fields</SelectItem>
                {fields.map(f => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {(selectedDestination !== 'all' || selectedLevel !== 'all' || selectedField !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedDestination('all');
                  setSelectedLevel('all');
                  setSelectedField('all');
                }}
                className={isDark ? 'text-white/70 hover:text-white' : ''}
              >
                {emptyState.clear_label || 'Clear Filters'}
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Featured Programs */}
      {featuredPrograms.length > 0 && !searchTerm && selectedDestination === 'all' && selectedLevel === 'all' && selectedField === 'all' && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className={`text-2xl font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Star className="w-6 h-6 text-yellow-500" />
              Featured Programs
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {featuredPrograms.map(program => (
                <Link
                  key={program.id}
                  to={`/programs/${program.slug}`}
                  className="group"
                  data-testid={`featured-program-${program.slug}`}
                >
                  <Card className={`h-full transition-all duration-300 hover:shadow-xl ${
                    isDark
                      ? 'bg-gradient-to-br from-ux-blue/20 to-ux-cyan/20 border-white/20 hover:border-ux-cyan/50'
                      : 'bg-gradient-to-br from-ux-blue/5 to-ux-cyan/5 border-ux-blue/20 hover:border-ux-blue/50'
                  }`}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge className="bg-yellow-500/20 text-yellow-600 border-yellow-500/30">
                          Featured
                        </Badge>
                        <Badge variant="outline" className={isDark ? 'border-white/30 text-white/80' : ''}>
                          {program.destination}
                        </Badge>
                      </div>
                      <h3 className={`text-xl font-bold mb-2 group-hover:text-ux-cyan transition-colors ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {program.title}
                      </h3>
                      <p className={`text-sm mb-4 line-clamp-2 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                        {program.tagline}
                      </p>
                      <div className="flex flex-wrap gap-3 text-sm">
                        <span className={`flex items-center gap-1 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                          <Clock className="w-4 h-4" />
                          {program.duration}
                        </span>
                        <span className={`flex items-center gap-1 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                          <GraduationCap className="w-4 h-4" />
                          {program.degree_level}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Programs */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {searchTerm || selectedDestination !== 'all' || selectedLevel !== 'all' || selectedField !== 'all'
                ? `Matching Programs (${filteredPrograms.length})`
                : 'All Programs'
              }
            </h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-ux-blue" />
            </div>
          ) : fetchError ? (
            <div className="text-center py-20" data-testid="programs-fetch-error">
              <BookOpen className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-white/20' : 'text-slate-300'}`} />
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Couldn't load programs
              </h3>
              <p className={`mb-6 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                We're having trouble reaching our servers. Please try again.
              </p>
              <Button onClick={() => window.location.reload()} data-testid="programs-retry-button">
                Retry
              </Button>
            </div>
          ) : filteredPrograms.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-white/20' : 'text-slate-300'}`} />
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`} data-testid="programs-empty-title">
                {emptyState.title}
              </h3>
              <p className={isDark ? 'text-white/60' : 'text-slate-500'}>
                {searchTerm ? 'Try a different search term' : (emptyState.subtitle || 'Programs will appear here once added')}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPrograms.map(program => (
                <Link
                  key={program.id}
                  to={`/programs/${program.slug}`}
                  className="group"
                  data-testid={`program-card-${program.slug}`}
                >
                  <Card className={`h-full transition-all duration-300 hover:shadow-lg ${
                    isDark
                      ? 'bg-white/5 border-white/10 hover:bg-white/10'
                      : 'bg-white hover:shadow-xl'
                  }`}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="outline" className={`${isDark ? 'border-ux-cyan/50 text-ux-cyan' : 'border-ux-blue/50 text-ux-blue'}`}>
                          {program.destination}
                        </Badge>
                        <Badge variant="secondary" className={isDark ? 'bg-white/10 text-white/70' : ''}>
                          {program.degree_level}
                        </Badge>
                        {program.is_featured && (
                          <Star className="w-4 h-4 text-yellow-500 ml-auto" />
                        )}
                      </div>

                      <h3 className={`text-lg font-bold mb-2 group-hover:text-ux-cyan transition-colors ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {program.title}
                      </h3>

                      <p className={`text-sm mb-4 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                        {program.field_of_study}
                      </p>

                      <div className="space-y-2 text-sm mb-4">
                        <div className={`flex items-center gap-2 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                          <Clock className="w-4 h-4 flex-shrink-0" />
                          <span>{program.duration}</span>
                        </div>
                        <div className={`flex items-center gap-2 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                          <DollarSign className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{program.avg_tuition}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-dashed mt-auto">
                        <span className={`text-xs ${isDark ? 'text-white/50 border-white/20' : 'text-slate-400 border-slate-200'}`}>
                          Intake: {program.intake_seasons?.join(', ')}
                        </span>
                        <ChevronRight className={`w-5 h-5 group-hover:translate-x-1 transition-transform ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`text-2xl md:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`} data-testid="programs-final-cta-title">
            {finalCta.title}
          </h2>
          <p className={`mb-8 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
            {finalCta.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-gradient-to-r from-ux-cyan to-ux-blue text-white"
              onClick={() => window.dispatchEvent(new CustomEvent('open-ai-chat'))}
              data-testid="ai-counselor-cta"
            >
              {finalCta.cta_primary_text}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              asChild
              className={isDark ? 'border-white/30 text-white hover:bg-white/10' : ''}
            >
              <Link to={finalCta.cta_secondary_link || '/contact'} data-testid="contact-cta">
                {finalCta.cta_secondary_text}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default ProgramsListPage;
